/**
 * India at the checkout (job #831).
 *
 * Tom, 2026-09-29 (r-2026-09-29-india-purchase-choices-9-single-language): an
 * Indian learner is offered nine single-language plans, Premium individual and
 * Family, each monthly or annual, all in rupees. Single-language purchases
 * stack with no exclusion: a deep link for one course pre-selects it, and the
 * learner can buy any other course instead or as well.
 *
 * WHO IS AN INDIAN LEARNER. Either signal is enough, as the brief puts it
 * ("country or eng_for_* course"): the course they are on or arrived for is one
 * of the nine English-for-India courses, or the phone's own time zone is
 * India's. The time zone is the country signal the browser gives for free, with
 * no request and no geo lookup. The same predicate decides what the picker
 * shows AND which price the checkout opens, and it reads nothing that can
 * change between the two, so a learner is always charged the price they saw.
 *
 * The prices themselves live in @ssi/core's indiaCatalog, the list the webhook
 * grants from.
 */
import {
  INDIA_COURSES,
  INDIA_PREMIUM,
  INDIA_FAMILY,
  INDIA_COURSE_MONTHLY_PAISE,
  INDIA_PREMIUM_MONTHLY_PAISE,
  offeredIndiaPriceId,
  isIndiaCourse,
  formatRupees,
  type UserEntitlement,
} from '@ssi/core'

const INDIA_TIME_ZONES = new Set(['Asia/Kolkata', 'Asia/Calcutta'])

export function deviceTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null
  } catch {
    return null
  }
}

export function isIndiaLearner(
  courseCode: string | null | undefined,
  timeZone: string | null = deviceTimeZone(),
): boolean {
  if (isIndiaCourse(courseCode)) return true
  return !!timeZone && INDIA_TIME_ZONES.has(timeZone)
}

export type IndiaCheckoutPlan = 'course' | 'premium' | 'family'

export type IndiaCheckoutPeriod = 'monthly' | 'quarterly' | 'annual'

/**
 * The INR price id for a choice, or null when the choice has none: a course
 * outside the India list, a plan the catalogue does not carry, a quarter on
 * anything but a course, or a price still a PLACEHOLDER_ until it is created
 * on live Paddle (job #447). Null means no buy button and no checkout, which
 * is how a placeholder fails closed.
 */
export function indiaPriceFor(
  plan: IndiaCheckoutPlan | string,
  period: IndiaCheckoutPeriod,
  courseCode: string | null | undefined,
): string | null {
  if (plan === 'course') {
    const c = INDIA_COURSES.find((x) => x.courseCode === courseCode)
    return c ? offeredIndiaPriceId(c[period]) : null
  }
  if (period === 'quarterly') return null
  if (plan === 'premium') return offeredIndiaPriceId(INDIA_PREMIUM[period])
  if (plan === 'family') return offeredIndiaPriceId(INDIA_FAMILY[period])
  return null
}

/**
 * The India courses this account already holds through an unexpired
 * entitlement: a single-course plan, a code or grant naming it, or a full
 * grant. The same reading checkCourseAccess makes. Premium and Family are
 * subscriptions, not entitlements, and are answered by isSubscribed, which
 * closes the picker before it opens.
 */
export function ownedIndiaCourses(
  entitlements: readonly UserEntitlement[] | null | undefined,
  now: Date = new Date(),
): Set<string> {
  const owned = new Set<string>()
  for (const e of entitlements ?? []) {
    if (e.expiresAt && new Date(e.expiresAt) <= now) continue
    if (e.accessType === 'full') return new Set(INDIA_COURSES.map((c) => c.courseCode))
    if (e.accessType === 'courses') {
      for (const code of e.grantedCourses ?? []) if (isIndiaCourse(code)) owned.add(code)
    }
  }
  return owned
}

/**
 * Which course the picker pre-selects: the one they arrived for when it is an
 * India course they do not already own, otherwise the first they do not own.
 * Null when they own all nine.
 */
export function preselectIndiaCourse(
  arrivedFor: string | null | undefined,
  owned: ReadonlySet<string>,
): string | null {
  if (arrivedFor && isIndiaCourse(arrivedFor) && !owned.has(arrivedFor)) return arrivedFor
  return INDIA_COURSES.find((c) => !owned.has(c.courseCode))?.courseCode ?? null
}

/** "Plans from ₹299/month. Cancel anytime." — the rupee form of the £15 from-lines. */
export function indiaPlansFromLine(t: (key: string) => string): string {
  return t('plans.indiaFrom').replace('{price}', formatRupees(INDIA_COURSE_MONTHLY_PAISE))
}

/**
 * The from-line over the course picker's Premium block. ₹299 is the price of
 * the English-for-India courses only; a list with none of them in it
 * (English speakers choosing Chinese, say) is sold at India Premium, so the
 * line names that price instead, ₹1,999 (IME, 2026-10-27).
 */
export function indiaPickerFromLine(
  t: (key: string) => string,
  listedCourseCodes: readonly string[],
): string {
  if (listedCourseCodes.some((c) => isIndiaCourse(c))) return indiaPlansFromLine(t)
  return t('plans.indiaFrom').replace('{price}', formatRupees(INDIA_PREMIUM_MONTHLY_PAISE))
}

/**
 * The one-course promise under the rupee price (job #181, red-team #133
 * finding 6): "₹299/month opens this one course, every round of it." The
 * card used to put the Premium bullets under the course price; a buyer who
 * paid ₹299 for "every course in 65+ languages" had a refund case.
 */
export function indiaOneCourseLine(t: (key: string) => string): string {
  return t('player.indiaOneCourseOpens').replace('{price}', formatRupees(INDIA_COURSE_MONTHLY_PAISE))
}
